/**
 * @fileoverview Organism: the plate, the phrase, and the invitation to draw.
 * @module components/organisms/MotivationStage
 */

import { Icon } from "@/components/atoms"
import { DrawHint, PhraseBoard } from "@/components/molecules"
import { formatRemaining } from "@/lib/domain"
import { AppMode, type Cooldown, type Phrase } from "@/types/types"

import { PeakArt } from "../PeakArt/PeakArt"
import { TestoArt } from "../TestoArt/TestoArt"

import "./MotivationStage.css"

/** Props accepted by {@link MotivationStage}. */
export interface MotivationStageProps {
	/** The active mode, which decides which plate is drawn. */
	readonly mode: AppMode
	/** The phrase on display, or null while none is resolved. */
	readonly phrase: Phrase | null
	/** Where the draw lock stands. */
	readonly cooldown: Cooldown
	/** True until the first phrase settles. */
	readonly isLoading: boolean
	/** True while the voice is mid-phrase. */
	readonly isSpeaking: boolean
	/** Draws a new phrase. */
	readonly onDraw: () => void
}

/**
 * The whole screen, minus the header and the action bar.
 *
 * @description
 * The illustration *is* the draw control — a real `<button>`, not a div with
 * a click handler, so it is reachable by keyboard, announces what it does,
 * and reports on its own when the one-minute lock makes it unavailable.
 *
 * @param props - Mode, phrase, cooldown, transient flags and the draw
 *   callback.
 * @returns The rendered stage.
 */
export function MotivationStage({
	mode,
	phrase,
	cooldown,
	isLoading,
	isSpeaking,
	onDraw,
}: MotivationStageProps) {
	const isLocked = !cooldown.isReady

	return (
		<div className={`motivation motivation--${mode}`}>
			<button
				type="button"
				className="motivation__plate"
				onClick={onDraw}
				disabled={isLocked || isLoading}
				aria-label={
					isLocked
						? `Nouvelle phrase disponible dans ${formatRemaining(cooldown.remainingMs)}`
						: "Tirer une nouvelle phrase"
				}
			>
				{mode === AppMode.PEAK ? <PeakArt /> : <TestoArt />}

				{isLocked && (
					<span className="motivation__lock" aria-hidden="true">
						<Icon name="lock" size={18} />
					</span>
				)}
			</button>

			<PhraseBoard
				phrase={phrase}
				mode={mode}
				isLoading={isLoading}
				isSpeaking={isSpeaking}
			/>

			<DrawHint cooldown={cooldown} />
		</div>
	)
}
